import React, { useState } from 'react';
import { Icon, getHelpCenterLinks } from 'venn-ui-kit';
import { Numbers } from 'venn-utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { FeeAdjustType, ModalMode, SubjectFee } from './types';

const SAVE_MODES = new Set<ModalMode>(['save', 'saveAs', 'saveOnLeave', 'saveOnPdfExport']);

type AdvisoryFeeCorrectionModalProps = {
  defaultFee: number;
  minFee: number;
  maxFee: number;
  subjectFees: SubjectFee[];
  actionsDisabled: boolean;
  mode: ModalMode;
  withDiscard: boolean;
  onApply: (feeAdjustType: FeeAdjustType) => void;
  onClose: () => void;
  onDiscard: () => void;
};

const SubjectRows = ({
  subjectFees,
  targetFee,
}: {
  subjectFees: SubjectFee[];
  targetFee: (subject: SubjectFee) => number;
}) => (
  <div className="overflow-y-auto ml-6 -mr-[10px] mt-2 pr-[10px]">
    {subjectFees.map((subjectFee, index) => (
      <div key={subjectFee.subjectId}>
        {index > 0 && <div className="border-0 border-t border-solid border-venn-grey-30" />}
        <div className="flex gap-[6px] justify-between items-center py-1">
          <span className="text-[14px] truncate">{subjectFee.subjectName}</span>
          <span className="text-[14px] text-nowrap">
            {Numbers.safeFormatPercentage(subjectFee.fee)}
            {' → '}
            <span className="font-bold">{Numbers.safeFormatPercentage(targetFee(subjectFee))}</span>
          </span>
        </div>
      </div>
    ))}
  </div>
);

export const AdvisoryFeeCorrectionModal = ({
  defaultFee,
  minFee,
  maxFee,
  subjectFees,
  actionsDisabled,
  mode,
  withDiscard,
  onClose,
  onDiscard,
  onApply,
}: AdvisoryFeeCorrectionModalProps) => {
  const [selectedOption, setSelectedOption] = useState<FeeAdjustType | undefined>(undefined);

  const canDiscardChanges = (mode === 'save' && withDiscard) || mode === 'saveOnLeave';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        // [&]:sm:max-w-[550px] overrides Dialog's default sm:max-w-lg (512px) constraint to enforce the design width.
        // !animate-none suppresses the entry animation — this is a blocking business-logic prompt, not a contextual panel.
        className="w-[550px] [&]:sm:max-w-[550px] max-h-[98vh] flex flex-col p-0 gap-0 rounded-none border-none shadow-[0px_6px_6px_0px_rgba(16,22,27,0.2)] !animate-none"
      >
        <DialogClose className="text-black ring-offset-background focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-[20px] right-[24px] rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-9">
          <Icon type="xmark" prefix="fal" className="text-[32px]" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="px-6 py-4 border-0 border-b border-solid border-venn-grey-30">
          <DialogTitle className="m-0 text-[32px] font-light leading-tight">
            Advisory fees outside workspace limits
          </DialogTitle>
        </div>

        <div className="flex min-h-0 flex-1 flex-col px-6 py-4">
          <RadioGroup
            disabled={actionsDisabled}
            // Empty string represents "no selection" for the controlled Radix RadioGroup.
            value={selectedOption ?? ''}
            onValueChange={(value) => setSelectedOption(value as FeeAdjustType)}
            className="flex min-h-0 flex-col gap-4"
          >
            <div className="flex min-h-0 flex-col">
              <div className="flex shrink-0 items-center gap-3">
                <RadioGroupItem
                  value="default"
                  state={selectedOption === 'default' ? 'checked' : 'unchecked'}
                  id="option-default"
                />
                <label htmlFor="option-default" className="text-[14px] font-bold cursor-pointer">
                  Set all invalid fees to the workspace default ({Numbers.safeFormatPercentage(defaultFee)})
                </label>
              </div>
              {selectedOption === 'default' && <SubjectRows subjectFees={subjectFees} targetFee={() => defaultFee} />}
            </div>

            <div className="flex min-h-0 flex-col">
              <div className="flex shrink-0 items-center gap-3">
                <RadioGroupItem
                  value="minmax"
                  state={selectedOption === 'minmax' ? 'checked' : 'unchecked'}
                  id="option-minmax"
                />
                <label htmlFor="option-minmax" className="text-[14px] font-bold cursor-pointer">
                  Set each invalid fee to the closest workspace min or max ({Numbers.safeFormatPercentage(minFee)}–
                  {Numbers.safeFormatPercentage(maxFee)})
                </label>
              </div>
              {selectedOption === 'minmax' && (
                <SubjectRows
                  subjectFees={subjectFees}
                  targetFee={(subject) => Math.min(Math.max(minFee, subject.fee), maxFee)}
                />
              )}
            </div>
          </RadioGroup>

          <p className="mt-4 text-[14px]">
            Fees can be further adjusted in the Inputs panel.{' '}
            <a
              href={getHelpCenterLinks().ADVISORY_FEES_HELP_HREF}
              target="_blank"
              rel="noreferrer"
              className="text-venn-dark-blue font-semibold hover:underline"
            >
              Learn more
            </a>
          </p>
        </div>

        <div className="px-6 py-3 flex justify-between items-center border-0 border-t border-solid border-venn-grey-30">
          <Button variant="outline" size="xl" disabled={actionsDisabled} onClick={onClose}>
            {mode !== 'info' ? 'Cancel' : 'Close'}
          </Button>
          <div className="flex gap-3">
            {canDiscardChanges && (
              <Button
                variant="outline"
                size="xl"
                disabled={actionsDisabled}
                className="border-venn-error text-venn-error hover:bg-venn-error/5"
                onClick={onDiscard}
              >
                Discard Changes
              </Button>
            )}
            <Button
              type="submit"
              disabled={!selectedOption || actionsDisabled}
              className="h-[40px] px-[15px] py-[10px] uppercase text-[11px] font-bold tracking-[1px] leading-none bg-venn-dark-blue hover:bg-venn-dark-blue/90 disabled:opacity-50"
              onClick={() => selectedOption && onApply(selectedOption)}
            >
              {SAVE_MODES.has(mode)
                ? 'Adjust Fees and Save'
                : mode === 'export'
                  ? 'Adjust Fees and Export'
                  : 'Adjust Fees'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
